import React, { useState, useEffect } from 'react';
import { HitApi } from '../../Api/ApiHit';
import { searchAppCategory, searchHomeSection, deleteHomeSection, searchUnit } from '../../constant/Constant';
import { Plus, Pencil, Trash2, GripVertical, LayoutGrid, ListIcon, Columns, Package } from 'lucide-react';
import toast from 'react-hot-toast';
import AddHomeSection from './AddHomeSection';

const DISPLAY_ICONS = { grid: <LayoutGrid className="w-3.5 h-3.5" />, carousel: <Columns className="w-3.5 h-3.5" />, list: <ListIcon className="w-3.5 h-3.5" /> };

const HomeServices = () => {
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);

  useEffect(() => {
    fetchCategories();
    fetchUnits();
  }, []);

  useEffect(() => {
    if (activeTab && selectedUnit) fetchSections();
  }, [activeTab, selectedUnit]);

  const fetchCategories = () => {
    setLoading(true);
    HitApi({ page: 1, limit: 100000, sortBy: 'sortOrder', sortOrder: 'asc' }, searchAppCategory)
      .then((res) => {
        const docs = res?.data?.docs || res?.data || [];
        if (Array.isArray(docs) && docs.length > 0) {
          setCategories(docs);
          setActiveTab(docs[0]._id);
        }
      })
      .catch((err) => console.error('Error fetching categories:', err))
      .finally(() => setLoading(false));
  };

  const fetchUnits = () => {
    HitApi({ page: 1, limit: 100000 }, searchUnit)
      .then((res) => {
        const docs = res?.data?.docs || res?.data || [];
        if (Array.isArray(docs) && docs.length > 0) {
          setUnits(docs);
          setSelectedUnit(docs[0]._id);
        }
      })
      .catch((err) => console.error('Error fetching units:', err));
  };

  const fetchSections = () => {
    setSectionsLoading(true);
    HitApi({
      page: 1,
      limit: 100000,
      sortBy: 'order',
      sortOrder: 'asc',
      appCategoryId: activeTab,
      unitId: selectedUnit
    }, searchHomeSection)
      .then((res) => {
        console.log('searchHomeSection response:', JSON.stringify(res));
        const docs = res?.data?.data?.docs || res?.data?.docs || res?.docs || res?.data || [];
        setSections(Array.isArray(docs) ? docs : []);
      })
      .catch((err) => console.error('Error fetching sections:', err))
      .finally(() => setSectionsLoading(false));
  };

  const handleDelete = () => {
    if (!deleteModal.id) return;
    HitApi({ _id: deleteModal.id }, deleteHomeSection)
      .then((res) => {
        if (res?.success || res?.statusCode === 200) {
          toast.success('Section deleted');
          fetchSections();
        } else {
          toast.error(res?.message || 'Failed to delete');
        }
      })
      .catch(() => toast.error('Failed to delete'))
      .finally(() => setDeleteModal({ open: false, id: null }));
  };

  const handleFormClose = (refresh) => {
    setShowForm(false);
    setEditData(null);
    if (refresh) fetchSections();
  };

  if (showForm) {
    return (
      <AddHomeSection
        appCategoryId={activeTab}
        unitId={selectedUnit}
        editData={editData}
        onClose={handleFormClose}
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="px-6 pt-6 pb-0 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Home Services</h1>
          <p className="text-sm text-gray-500 mt-1">Manage home service sections for the mobile app</p>
        </div>
        {/* Unit Selector */}
        {units.length > 0 && (
          <select
            value={selectedUnit || ''}
            onChange={(e) => setSelectedUnit(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
          >
            {units.map((u) => (
              <option key={u._id} value={u._id}>{u.name || u.unitName || u._id}</option>
            ))}
          </select>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
        </div>
      )}

      {/* Tabs */}
      {!loading && categories.length > 0 && (
        <div className="px-6 mt-4 border-b border-gray-200">
          <div className="flex gap-0 overflow-x-auto">
            {categories.map((cat) => (
              <button
                key={cat._id}
                onClick={() => setActiveTab(cat._id)}
                className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === cat._id
                    ? 'border-black text-black'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab && selectedUnit && (
        <div className="p-6">
          {/* Add Button */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Sections</h2>
            <button
              onClick={() => { setEditData(null); setShowForm(true); }}
              className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Section
            </button>
          </div>

          {/* Sections Loading */}
          {sectionsLoading && (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black"></div>
            </div>
          )}

          {/* Empty */}
          {!sectionsLoading && sections.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg shadow border border-gray-100">
              <p className="text-gray-400">No sections added for this category</p>
              <button
                onClick={() => { setEditData(null); setShowForm(true); }}
                className="mt-3 text-sm text-black underline"
              >
                Create your first section
              </button>
            </div>
          )}

          {/* Section List */}
          {!sectionsLoading && sections.length > 0 && (
            <div className="space-y-3">
              {sections.map((section) => (
                <div key={section._id} className="bg-white rounded-lg shadow border border-gray-100 p-4 group hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <GripVertical className="w-4 h-4 text-gray-300 mt-1" />
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">{section.title}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${section.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {section.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        {section.subtitle && <p className="text-xs text-gray-500 mt-0.5">{section.subtitle}</p>}
                        <div className="flex items-center gap-3 mt-2">
                          <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {DISPLAY_ICONS[section.displayType]} {section.displayType}
                          </span>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {section.sectionType}
                          </span>
                          <span className="text-xs text-gray-500">
                            {section.sectionType === 'services'
                              ? `${section.services?.length || 0} services`
                              : section.sectionType === 'packages'
                              ? `${section.packages?.length || 0} packages`
                              : `${section.categories?.length || 0} categories`
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => { setEditData(section); setShowForm(true); }}
                        className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setDeleteModal({ open: true, id: section._id })}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Section</h3>
            <p className="text-sm text-gray-600 mb-4">Are you sure? This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteModal({ open: false, id: null })} className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeServices;
